import { useEffect } from 'react'
import { NavLink, Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, Users, SlidersHorizontal, BookOpen, MoreHorizontal, KeyRound, Clapperboard, CalendarDays, Inbox, Phone, LogOut, Home, ChevronLeft, CalendarPlus, Settings2, DollarSign, CalendarSearch } from 'lucide-react'
import { api } from '../../lib/api.js'
import { cn } from '../../lib/cn.js'
import useMediaQuery, { LANDSCAPE_PHONE } from '../../lib/useMediaQuery.js'
import { maybeAutoSync, maybeSaveCalendars, lastSyncedAt } from '../../lib/offlineSync.js'
import { useOnline } from '../../lib/useOnline.js'
import { syncPush } from '../../lib/push.js'
import EqMark from '../EqMark.jsx'
import { BOX_NAME } from '../../lib/suggestionBox.js'
import { useBuilderHistory } from '../../features/assistant/nav.js'

/**
 * The shell, Soft Ink v2 — an app frame, not a page: brand bar on top and
 * tab bar below are static rows of a fixed-height column, and ONLY the
 * middle pane scrolls. Nothing can scroll the navigation away, on any
 * browser. Desktop swaps the bars for the grouped rail.
 */

// Tab order per Charles (2026-08-03): Search first — the fastest way to
// anything — then Guides, Studios, Clients, More. Since 2026-09-29 Search
// sits in a narrow slot (its short label fits), which is what makes room
// for Logins — people reach for logins far more often than anything else
// that used to sit behind More. The label came back the same day
// (Charles: there's room for the word).
const TABS = [
  { to: '/search', label: 'Search', Icon: Search, narrow: true },
  { to: '/guides', label: 'Guides', Icon: BookOpen },
  { to: '/studios', label: 'Studio Info', Icon: SlidersHorizontal },
  { to: '/clients', label: 'Client Info', Icon: Users },
  { to: '/logins', label: 'Logins', Icon: KeyRound },
  { to: '/more', label: 'More', Icon: MoreHorizontal },
]

export default function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const spaces = useQuery({ queryKey: ['spaces'], queryFn: () => api('/spaces') })

  const user = me.data?.user

  // Red dot on More (Charles, 2026-08-04): the admin's ambient "there's
  // something in the log check" signal — red is allowed here, it's an
  // alert. Shares the ['sl-check'] cache with the check screens; server
  // answers non-admins with an empty list.
  const check = useQuery({
    queryKey: ['sl-check'],
    queryFn: () => api('/log/check'),
    enabled: user?.role === 'admin',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 15 * 60 * 1000,
  })
  const checkAlert = (check.data?.days || []).reduce((n, d) => n + d.deficit, 0) > 0
  // Navy dot on the Suggestions rail link: replies/verdicts on your
  // suggestions since you last opened the box (shares ['box-activity']
  // with the hub tile; navy because it's news, not an alarm).
  const boxActivity = useQuery({
    queryKey: ['box-activity'],
    queryFn: () => api('/box/activity'),
    enabled: Boolean(user),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  })
  const boxAlert = (boxActivity.data?.count || 0) > 0
  // Session Builder: bookings typed into Google not yet tidied (admins only).
  // Re-asked every few seconds while a calendar is still arriving (the count is short until then), every 15 s after a failed ask.
  const untidy = useQuery({ queryKey: ['sa-untidy'], queryFn: () => api('/assistant/untidy'), enabled: user?.role === 'admin', staleTime: 5 * 60 * 1000, refetchOnWindowFocus: true, refetchInterval: (q) => (q.state.status === 'error' ? 15 * 1000 : q.state.data?.feedsLoading ? 3000 : 5 * 60 * 1000) })
  const toTidy = untidy.data?.count || 0
  const billing = useQuery({ queryKey: ['sa-billing-count'], queryFn: () => api('/assistant/billing/count'), enabled: user?.role === 'admin', staleTime: 5 * 60 * 1000, refetchOnWindowFocus: true, refetchInterval: (q) => (q.state.status === 'error' ? 15 * 1000 : q.state.data?.feedsLoading ? 3000 : 5 * 60 * 1000) })
  const toBill = billing.data?.count || 0
  const railSpaces = (spaces.data?.spaces || []).filter(
    (s) => !['clients', 'logins'].includes(s.kind) // clients + logins have dedicated entries
  )
  // '/log' or '/log/…' only — a bare startsWith('/log') also catches
  // '/logins', which mislabeled the Logins page as Session Log.
  const inSessionLog = location.pathname === '/log' || location.pathname.startsWith('/log/')
  const inCalendar = location.pathname === '/calendar' || location.pathname.startsWith('/calendar/')
  const inBox = location.pathname === '/box' || location.pathname.startsWith('/box/')
  const inPhone = location.pathname === '/phone' || location.pathname.startsWith('/phone/')
  const inAssistant = location.pathname === '/assistant' || location.pathname.startsWith('/assistant/')
  // Which Session Builder screen each one was opened from — so Back steps
  // down instead of piling new screens on (features/assistant/nav.js).
  useBuilderHistory()
  const onHome = location.pathname === '/'
  // The tab bar is CDM Information's navigation — the hub and the other
  // apps aren't that app, so none of them show it (Charles, 2026-08-03).
  const showTabBar = !onHome && !inSessionLog && !inCalendar && !inBox && !inPhone && !inAssistant
  // A phone held sideways is wider than the desktop breakpoint, so the
  // rail would appear and eat a third of the screen. In the Session
  // Builder that width is for the five-day view (Charles, 2026-10-05: "no
  // need for the sidebar — just use all the space for calendar"): no rail,
  // the phone's brand bar (the way back), and the content edge to edge.
  const landscapePhone = useMediaQuery(LANDSCAPE_PHONE)
  const fullBleed = inAssistant && landscapePhone
  // The Session Builder's calendar uses the whole width of a big screen
  // (Charles, 2026-10-08: "use more of the screen on big screens like
  // mine"); its list keeps a reading column of its own.
  const builderHome = location.pathname === '/assistant'
  // No rail anywhere in the Session Builder (Charles, 2026-10-08: "remove
  // the sidebar with the session builder and have the sound bars … to get
  // back to the home page"). The brand bar — the sound bars, a tap home —
  // shows on every screen size instead; on a big screen the calendar's own
  // bar carries the sound bars, so the brand bar stands down there.
  const brandClass = fullBleed ? '' : inAssistant ? (builderHome ? 'lg:hidden' : '') : 'md:hidden'
  // React Router tracks its position in history.state.idx — 0 means this is
  // the first in-app entry, so there is nothing in-app to go back to.
  // location in the deps above re-renders us on every navigation.
  const canGoBack = (window.history.state?.idx ?? 0) > 0

  // Keep the offline copy of CDM Information current: silent refresh on
  // launch when the cached copy is older than 6 h.
  useEffect(() => {
    maybeAutoSync()
  }, [])
  // And the calendars' copy, for a look with no service: at launch and on
  // the way into either calendar (it does the work at most every 15 min).
  useEffect(() => {
    maybeSaveCalendars(user)
  }, [user, inCalendar, inAssistant])
  // Push: re-post this device's subscription so the server copy follows
  // whoever is signed in and any renewal the phone did on its own.
  useEffect(() => {
    if (user) syncPush()
  }, [user])

  async function logout() {
    await api('/auth/logout', { method: 'POST' }).catch(() => {})
    qc.setQueryData(['me'], null)
  }

  // Session Builder v3 is a full-screen app of its own (its own bar, its
  // own way home) — no rail, brand bar or tab bar around it.
  if (location.pathname === '/builder' || location.pathname.startsWith('/builder/')) {
    return (
      <div className="h-dvh overflow-hidden bg-grouped">
        <Outlet />
      </div>
    )
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-grouped">
      {/* ── Desktop nav rail ── */}
      <aside className={cn('hidden h-full w-60 shrink-0 flex-col border-r border-hairline bg-white', !fullBleed && !inAssistant && 'md:flex')}>
        <Link to="/" className="block px-5 pb-4 pt-6">
          <img src="/cdm-logo.png" alt="CDM Sound Studios" className="h-14 w-auto" />
        </Link>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3">
          <RailLink to="/" label="Home" Icon={Home} end />
          {/* Search sits with Home, above the app groups — it reaches
              everything your account can see, not one section. */}
          <RailLink to="/search" label="Search" Icon={Search} />
          <div className="section-header px-3 pb-1 pt-5">CDM Information</div>
          {/* Same order as the tab bar: Guides, Studios, Clients, Search. */}
          {['guides', 'studios'].map((slug) => {
            const s = railSpaces.find((x) => x.slug === slug)
            return s ? (
              <RailLink key={s.slug} to={`/${s.slug}`} label={s.name} emoji={s.icon} badge={s.pageCount || undefined} />
            ) : null
          })}
          <RailLink to="/clients" label="Client Info" Icon={Users} />
          <RailLink to="/logins" label="Logins" Icon={KeyRound} />
          {railSpaces
            .filter((s) => !['guides', 'studios'].includes(s.slug))
            .map((s) => (
              <RailLink
                key={s.slug}
                to={`/${s.slug}`}
                label={s.name}
                emoji={s.icon}
                badge={s.pageCount || undefined}
              />
            ))}
          <div className="section-header px-3 pb-1 pt-5">CDM Session Log</div>
          <RailLink to="/log" label="Sessions" Icon={Clapperboard} alert={checkAlert} />
          {/* Session Builder sits just below the Session Log, as on the hub
              (Charles, 2026-10-14). */}
          {user?.role === 'admin' && (
            <>
              <div className="section-header px-3 pb-1 pt-5">Session Builder</div>
              <RailLink to="/assistant" label="Sessions" Icon={CalendarPlus} badge={toTidy || undefined} end />
              <RailLink to="/assistant/find" label="Find a time" Icon={CalendarSearch} />
              <RailLink to="/assistant/billing" label="Billing" Icon={DollarSign} badge={toBill || undefined} />
              <RailLink to="/assistant/calendars" label="Calendars" Icon={Settings2} />
            </>
          )}
          <div className="section-header px-3 pb-1 pt-5">CDM Calendar</div>
          <RailLink to="/calendar" label="Today" Icon={CalendarDays} />
          <div className="section-header truncate px-3 pb-1 pt-5">{BOX_NAME}</div>
          <RailLink to="/box" label="Suggestions" Icon={Inbox} alert={boxAlert} alertClass="bg-navy" />
          <div className="section-header px-3 pb-1 pt-5">Phone Log</div>
          <RailLink to="/phone" label="Calls" Icon={Phone} />
        </nav>

        <div className="border-t border-hairline p-3">
          <div className="flex items-center justify-between rounded-lg px-2 py-1.5">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-ink">{user?.name}</div>
              <div className="truncate text-xs text-muted">{user?.email}</div>
            </div>
            <button onClick={logout} title="Sign out" className="tap flex items-center justify-center rounded-lg text-muted hover:bg-black/5 hover:text-ink">
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Phone: brand bar / scrolling pane / tab bar ── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className={cn('flex shrink-0 items-stretch border-b border-hairline bg-white', brandClass)}
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', height: 'calc(44px + env(safe-area-inset-top, 0px))' }}
        >
          {/* The installed app has no browser chrome, so in-app links need
              their own way back — a history Back, iOS-style, far left.
              (Charles, 2026-08-02: tapping into a page from a page and
              losing your place was the complaint.) */}
          {canGoBack && (
            <button
              onClick={() => navigate(-1)}
              aria-label="Back"
              className="flex items-center pl-2.5 pr-1 text-navy active:opacity-60"
            >
              <ChevronLeft size={24} strokeWidth={2.2} />
            </button>
          )}
          <button
            onClick={() => navigate('/')}
            className={cn('flex min-w-0 flex-1 items-center justify-between pr-4', canGoBack ? 'pl-1.5' : 'pl-4')}
            aria-label="Back to home"
          >
            <span className="flex items-center gap-2">
              <EqMark />
              <span className="truncate font-serif text-[15px] font-semibold text-ink">CDM Sound Studios</span>
            </span>
            {!onHome && (
              <span className="section-header shrink-0 pl-2">
                {inSessionLog ? 'Session Log' : inCalendar ? 'Calendar' : inBox ? 'Suggestions' : inPhone ? 'Phone Log' : inAssistant ? 'Session Builder' : 'Information'}
              </span>
            )}
          </button>
          {/* Hub only: the tab bar is hidden here, so search gets its own
              magnifier top-right (Charles, 2026-09-13). Elsewhere the
              Search tab / rail link already covers it. */}
          {onHome && (
            <Link
              to="/search"
              aria-label="Search"
              className="flex items-center pl-2 pr-4 text-navy active:opacity-60"
            >
              <Search size={22} strokeWidth={2} />
            </Link>
          )}
        </div>

        <OfflineNotice inSessionLog={inSessionLog} ownNote={inCalendar || builderHome} />

        {/* Without the tab bar the scroll pane meets the screen edge, so it
            takes over clearing the iOS home indicator. */}
        <main className={cn('min-h-0 min-w-0 flex-1 scroll-pane', !showTabBar && 'safe-bottom')}>
          {/* Phone keeps the tight column; desktop opens up to use the
              screen (Charles, 2026-08-06 — tables were clipped inside a
              768px column with acres of margin). */}
          <div className={cn('mx-auto', fullBleed ? 'max-w-none px-3 pb-6 pt-2' : builderHome ? 'max-w-none px-4 pb-10 pt-4 md:px-6 md:pb-6 md:pt-4' : 'max-w-3xl px-4 pb-10 pt-4 md:max-w-6xl md:px-10 md:pb-16 md:pt-10')}>
            <Outlet />
          </div>
        </main>

        {showTabBar && (
        <nav className="shrink-0 border-t border-hairline bg-white safe-bottom md:hidden">
          <div className="flex">
            {TABS.map(({ to, label, Icon, narrow }) => {
              const active =
                location.pathname === to ||
                (to !== '/search' && location.pathname.startsWith(to + '/')) ||
                // More owns Information's overflow; /log is the other app,
                // so no Information tab lights up there.
                (to === '/more' && ['/procedures', '/facility', '/post', '/library', '/management'].some((p) => location.pathname.startsWith(p)))
              return (
                <NavLink
                  key={to}
                  to={to}
                  className={cn(
                    'tap flex flex-col items-center gap-0.5 pb-1 pt-2 text-[10px] font-medium',
                    narrow ? 'w-12 shrink-0' : 'min-w-0 flex-1',
                    active ? 'text-navy' : 'text-faint'
                  )}
                >
                  <span className="relative">
                    <Icon size={22} strokeWidth={active ? 2.2 : 1.8} />
                    {to === '/more' && checkAlert && (
                      <span className="absolute -right-1 -top-0.5 h-2 w-2 rounded-full bg-danger" />
                    )}
                  </span>
                  <span className="whitespace-nowrap">{label}</span>
                  {/* tally dot — navy marks the active section */}
                  <span className={cn('h-1 w-1 rounded-full', active ? 'bg-navy' : 'bg-transparent')} />
                </NavLink>
              )
            })}
          </div>
        </nav>
        )}
      </div>
    </div>
  )
}

/**
 * Slim status row under the brand bar, only while offline. Information is
 * readable from the synced copy (and says how old it is); the Session Log
 * needs a connection, and the notice says that instead of letting screens
 * error vaguely. Ink-on-white keeps red for armed things only.
 */
function OfflineNotice({ inSessionLog, ownNote = false }) {
  const online = useOnline()
  // The calendars say it themselves, with the time their copy was saved
  // (components/SavedCopyNote.jsx) — one notice, not two.
  if (online || ownNote) return null
  return (
    <div className="shrink-0 border-b border-hairline bg-ink py-1.5 text-center text-xs text-white">
      {inSessionLog
        ? 'Offline — the Session Log needs a connection'
        : `Offline — viewing your synced copy${syncedAgo()}`}
    </div>
  )
}

function syncedAgo() {
  const t = lastSyncedAt()
  if (!t) return ''
  const mins = Math.round((Date.now() - t) / 60000)
  if (mins < 2) return ' · synced just now'
  if (mins < 60) return ` · synced ${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 48) return ` · synced ${hours} h ago`
  return ` · synced ${Math.round(hours / 24)} days ago`
}

function RailLink({ to, label, Icon, emoji, badge, end, alert = false, alertClass = 'bg-danger' }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm',
          isActive ? 'font-semibold text-ink' : 'text-ink/80 hover:bg-grouped'
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* tally dot marks the active section — navy = wayfinding */}
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', isActive ? 'bg-navy' : 'bg-transparent')} />
          {Icon ? <Icon size={17} className="shrink-0 text-muted" /> : <span className="w-[17px] text-center text-sm leading-none grayscale">{emoji}</span>}
          <span className="flex-1 truncate">{label}</span>
          {alert && <span className={cn('h-2 w-2 shrink-0 rounded-full', alertClass)} />}
          {badge !== undefined && <span className="text-xs tabular-nums text-faint">{badge}</span>}
        </>
      )}
    </NavLink>
  )
}
